import React, { ReactNode, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Tab, Tabs } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useRewardsPortfoliosQuery } from 'hooks/queries/Rewards/useRewardsPortfoliosQuery'
import { useRewardsProductsQuery } from 'hooks/queries/Rewards/useRewardsProductsQuery'
import { ActionIds } from 'types/types'
import { RewardsManage } from './components/RewardsManage'
import { RewardsPageHeader } from './components/RewardsPageHeader'
import { RewardsSheetProvider } from './context/RewardsSheetProvider'
import { RewardsTabs, useRewardsTabs } from './hooks/useRewardsTabs'
import useRewardsTracking from './hooks/useRewardsTracking'
import { useRewardsWallets } from './hooks/useRewardsWallets'
import { RewardsDiscover } from './RewardsDiscover'

export const Rewards = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { trackPageView } = useRewardsTracking()

  useEffect(() => {
    trackPageView('home')
  }, [])

  const { activeTab, handleChangeTab } = useRewardsTabs()

  const { data: rewardsProductsData, isLoading } = useRewardsProductsQuery()
  const rewardsProducts = rewardsProductsData?.content ?? []
  const rewardsProductCurrencies =
    rewardsProducts.map(product => product?.currency)?.filter(Boolean) ?? []

  const { data: rewardsPortfolios } = useRewardsPortfoliosQuery()
  const userPortfolios = rewardsPortfolios?.content ?? []
  const hasPortfolios = userPortfolios.length > 0

  const { rewardsWallets, isLoadingWallets } = useRewardsWallets(rewardsProductCurrencies)
  const hasNoWallets = rewardsWallets?.length === 0

  const handleSeeDetails = (currency: string) => {
    navigate(`${PathNames.REWARDS}/${currency}`)
  }

  const handleAddWallet = () => {
    navigate(`${PathNames.WALLETS}?action=${ActionIds.WALLET_CREATE}`)
  }

  const DiscoverTabContent = useMemo(
    (): ReactNode => (
      <RewardsDiscover
        onSeeDetails={handleSeeDetails}
        onAddWallet={handleAddWallet}
        rewardsProducts={rewardsProducts}
        isLoading={isLoading || isLoadingWallets}
        hasNoWallets={hasNoWallets}
      />
    ),
    [handleSeeDetails, rewardsProducts, isLoading]
  )

  const rewardsTabs = useMemo(
    (): Tab[] => [
      {
        header: t('rewards.tabs.mangedPortfolios'),
        id: RewardsTabs.MANAGED_PORTFOLIOS,
        content: <RewardsManage userPortfolios={userPortfolios} />,
      },
      {
        header: t('rewards.tabs.discover'),
        id: RewardsTabs.DISCOVER,
        content: DiscoverTabContent,
      },
    ],
    [t, handleSeeDetails, rewardsProducts, isLoading]
  )

  return (
    <RewardsSheetProvider userPortfolios={userPortfolios} rewardsProducts={rewardsProducts}>
      <RewardsPageHeader showWithdrawButton={hasPortfolios} showDepositButton={!hasNoWallets} />
      {hasPortfolios ? (
        <Tabs defaultTabId={activeTab} tabs={rewardsTabs} onChange={handleChangeTab} />
      ) : (
        DiscoverTabContent
      )}
    </RewardsSheetProvider>
  )
}
